/**
 * /explore — Batch 2 (2026-08-07) — Traveler-facing Explore.
 *
 * History:
 *   • Phase 5 (2026-07-27): Google Places / discover swipe retired,
 *     Hotelbeds became the sole content provider — HotelbedsExplore*
 *     replaced the destinations grid + swipe deck.
 *   • Batch 1 (2026-08-07): Hotelbeds firewalled to `cert_reviewer`
 *     role, Stay22 became the traveler search backend at `/hotels`.
 *   • Batch 2 amendment (2026-08-07 — this file): the Stay22 CTA
 *     hero, subtitle "Live availability from Stay22 …", commission
 *     disclosure pill, and flights/cars CTA widgets are removed
 *     from /explore per user request. Traveler layout now reads:
 *         Hero title → Filters → Featured destinations (ExploreGrid,
 *         backed by /discover / travelbio_destinations — non-HB).
 *     Cert-reviewer layout (unchanged) reads:
 *         Hero title → HotelbedsExploreSearch → Filters
 *          → HotelbedsExploreCards → cert-mode side note.
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, Luggage, SlidersHorizontal, X as XIcon } from "lucide-react";
import { SectionRibbon } from "@/components/AppShell";
import HotelbedsExploreSearch from "@/components/HotelbedsExploreSearch";
import HotelbedsExploreCards from "@/components/HotelbedsExploreCards";
import PhaseFourFiltersSheet from "@/components/PhaseFourFiltersSheet";
import ExploreGrid from "@/components/ExploreGrid";
import InlineStaySearch from "@/components/InlineStaySearch";
import GenericBookingTilesStrip from "@/components/explore/GenericBookingTilesStrip";
import { useCertMode } from "@/hooks/useCertMode";

const HERO_IMAGE_URL =
  "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920&q=80&auto=format&fit=crop";

const DEFAULT_FILTERS = {
  vibes: [],
  minBudget: "",
  maxBudget: "",
  priceUnit: "per_night",
  propertyTypes: [],
  city: "",
  state: "",
  country: "",
  radius: null,
};

export default function ExploreFiltersPage() {
  const nav = useNavigate();
  const { active: certMode } = useCertMode();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeChips = useMemo(() => {
    const chips = [];
    if (filters.minBudget || filters.maxBudget) {
      const range =
        filters.minBudget && filters.maxBudget ? `$${filters.minBudget} – $${filters.maxBudget}` :
        filters.maxBudget ? `≤ $${filters.maxBudget}` :
        `≥ $${filters.minBudget}`;
      chips.push({ key: "budget", label: range });
    }
    filters.propertyTypes.forEach((p) => chips.push({
      key: `p_${p}`,
      label: p.replace(/_/g, " "),
    }));
    if (filters.city || filters.state || filters.country) {
      chips.push({
        key: "loc",
        label: [filters.city, filters.state, filters.country].filter(Boolean).join(", "),
      });
    }
    return chips;
  }, [filters]);

  const filtersRow = (
    <div className="flex flex-wrap items-center gap-2" data-testid="explore-filters-row">
      <button
        type="button"
        data-testid="explore-filters-open"
        onClick={() => setFiltersOpen(true)}
        className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm hover:brightness-110 tb-tap"
      >
        <SlidersHorizontal className="w-4 h-4" />
        {activeChips.length > 0 ? `Filters (${activeChips.length})` : "Filters"}
      </button>
      {activeChips.map((c) => (
        <span
          key={c.key}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tb-navy/[0.04] border border-tb-navy/15 font-hand text-tb-navy/80 text-xs"
        >
          {c.label}
        </span>
      ))}
      {activeChips.length > 0 && (
        <button
          type="button"
          data-testid="explore-filters-clear"
          onClick={() => setFilters(DEFAULT_FILTERS)}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-tb-navy/15 text-tb-navy/60 font-hand text-xs hover:border-tb-navy hover:text-tb-navy tb-tap"
        >
          <XIcon className="w-3 h-3" /> clear
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <SectionRibbon>Explore</SectionRibbon>

      {/* ── 1. Hero — title + Trips/Saved pills ── */}
      <div
        className="relative w-full overflow-hidden"
        style={{ height: "clamp(240px, 36vh, 340px)" }}
      >
        <img
          src={HERO_IMAGE_URL}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden="true"
          loading="eager"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(15,39,64,0.35) 0%, rgba(15,39,64,0.75) 100%)",
          }}
          aria-hidden="true"
        />

        <div className="absolute top-4 left-4 flex flex-wrap gap-2 z-10">
          <button
            type="button"
            onClick={() => nav("/booked")}
            data-testid="explore-hero-pill-trips"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-tb-navy/70 backdrop-blur-sm border-2 border-white/40 text-white font-hand text-xs hover:border-tb-gold tb-tap"
          >
            <Luggage className="w-3.5 h-3.5" /> Trips
          </button>
          <button
            type="button"
            onClick={() => nav("/bookmarks")}
            data-testid="explore-hero-pill-saved"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-tb-navy/70 backdrop-blur-sm border-2 border-white/40 text-white font-hand text-xs hover:border-tb-gold tb-tap"
          >
            <Bookmark className="w-3.5 h-3.5" /> Saved
          </button>
        </div>

        <div className="relative h-full flex flex-col items-center justify-center text-center px-4">
          <h1
            data-testid="explore-hero-title"
            className="font-serif-it italic leading-[1.02] drop-shadow-lg"
            style={{
              color: "#f5f0e6",
              fontSize: "clamp(2.5rem, 6vw, 4.5rem)",
              textWrap: "balance",
            }}
          >
            Where to next?
          </h1>
        </div>
      </div>

      {/* ── 2. Cert-reviewer only: Hotelbeds inventory search hero.
              Travelers see the InlineStaySearch card below the hero,
              which runs `/api/accommodations/search` inline (no route
              change) and renders results with a Grid/Swipe toggle.
              The Filters pill is passed to InlineStaySearch as an
              `interstitial` slot so it sits directly under the search
              card, above the results — matching the pre-Stay22
              layout. */}
      {certMode ? (
        <div className="max-w-7xl mx-auto px-4 md:px-8 -mt-14 md:-mt-16 relative z-10">
          <HotelbedsExploreSearch />
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 md:px-8 -mt-14 md:-mt-16 relative z-10">
          <InlineStaySearch interstitial={filtersRow} filters={filters} />
        </div>
      )}

      {/* ── 3. Featured destinations — non-Stay22 grid.
              For cert-reviewer: filters pill stays here above the
              Hotelbeds card grid (existing behaviour).
              For traveler: the filters pill has moved into
              InlineStaySearch's slot above; below we just render the
              featured destinations grid (silent when empty). */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-10 space-y-5">
        {certMode && filtersRow}

        {/* Generic booking-type CTAs (2026-08-17) — car rental +
            flight tiles.  Always shown when their admin card is
            active; not hidden by filter pills / property-type
            filters.  Traveler-only. */}
        {!certMode && <GenericBookingTilesStrip />}

        {/* Featured destinations grid.
            Cert-reviewer keeps HotelbedsExploreCards; everyone else
            sees the ExploreGrid (backed by /discover, non-HB). */}
        {certMode ? (
          <HotelbedsExploreCards filters={filters} />
        ) : (
          <ExploreGrid filters={filters} />
        )}
      </div>

      {/* Filters sheet — mounts lazily */}
      {filtersOpen && (
        <PhaseFourFiltersSheet
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          value={filters}
          onApply={(draft) => {
            setFilters(draft);
            setFiltersOpen(false);
          }}
          onReset={() => {
            setFilters(DEFAULT_FILTERS);
            setFiltersOpen(false);
          }}
        />
      )}
    </div>
  );
}
